import { Alert } from 'antd'
import { createFieldSetterWithDefault } from 'features/libra/stepComponents/niiReport/createFieldSetter'
import { NiiEligibilitySelect } from 'features/libra/stepComponents/niiReport/EligibilityTag'
import { EditableStringList } from 'features/libra/stepComponents/niiReport/NiiFieldComponents'
import { useNiiReportData } from 'features/libra/stepComponents/niiReport/NiiReportDataContext'
import {
  DescriptionItem,
  OptionalDescriptions,
} from 'features/libra/stepComponents/niiReport/OptionalDescriptions'
import { useReferenceValues } from 'features/libra/stepComponents/niiReport/referenceFields'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'

import { StyledCard } from './cardsStyles'

const WarningBanner = styled(Alert)`
  margin-bottom: 1rem;
`

const ConclusionCautionText =
  'This NII inspection report is only valid on the next scheduled inspection assuming there are no changes to process conditions and/or expected degradation. Any changes to expected degradation will require an updated NII assessment. The results of the NII inspection should be reviewed in accordance with HOIS for conformation to expectations, and these results will feed back into the process to determine if any additional actions are required.'

export const RawNiiConclusionCard: FC = () => {
  const { niiData, setNiiData, isLoading } = useNiiReportData()
  const [setEligibility, setRecommendations] = useMemo(
    () => [
      createFieldSetterWithDefault('eligibility', setNiiData, 'unclear'),
      createFieldSetterWithDefault('recommendations', setNiiData, []),
    ],
    [setNiiData]
  )

  const eligibility = useMemo(
    () => niiData?.eligibility ?? 'unclear',
    [niiData?.eligibility]
  )

  const recommendations = useMemo(
    () => niiData?.recommendations ?? [],
    [niiData?.recommendations]
  )

  const { values: recommendationsReferenceValues } =
    useReferenceValues('recommendations')

  const items = useMemo<ReadonlyArray<DescriptionItem>>(
    () => [
      [
        'Eligibility',
        <NiiEligibilitySelect
          key='eligibility'
          eligibility={eligibility}
          setEligibility={setEligibility}
        />,
      ] as const,
      [
        'Recommendations',
        <EditableStringList
          key='recommendations'
          items={recommendations}
          setItems={setRecommendations}
          referenceValues={recommendationsReferenceValues}
          notes
        />,
      ] as const,
    ],
    [
      eligibility,
      recommendations,
      setEligibility,
      setRecommendations,
      recommendationsReferenceValues,
    ]
  )

  return (
    <StyledCard title='Conclusion'>
      <WarningBanner title={ConclusionCautionText} type='warning' showIcon />
      <OptionalDescriptions isLoading={isLoading} items={items} />
    </StyledCard>
  )
}

export const NiiConclusionCard = React.memo(RawNiiConclusionCard)
